import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useLogin } from '../hooks/useAuth'
import SocialLoginButtons from '../components/SocialLoginButtons'
import SEOHead from '../components/SEOHead'

export default function LoginPage() {
  const navigate       = useNavigate()
  const [searchParams] = useSearchParams()
  const oauthError     = searchParams.get('error')

  const { mutate: login, isPending, error } = useLogin()
  const [form, setForm] = useState({ email: '', password: '' })

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const redirectTo = searchParams.get('redirect') ?? '/book'

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    login(form, { onSuccess: () => navigate(redirectTo) })
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-16 bg-gray-50">
      <SEOHead
        title="Sign In"
        description="Sign in to your Nala account to manage your pets and appointments."
        noIndex
      />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-gray-500 mt-2">Sign in to manage your pets and appointments.</p>
        </div>

        {/* OAuth error banner (e.g. user cancelled or provider rejected) */}
        {oauthError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            {decodeURIComponent(oauthError)}
          </div>
        )}

        <div className="card space-y-6">
          {/* ── Social sign-in ───────────────────────────────────────────── */}
          <SocialLoginButtons action="sign_in" />

          {/* ── Divider ──────────────────────────────────────────────────── */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400 uppercase tracking-wide">or</span>
            </div>
          </div>

          {/* ── Email / password ─────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="label" htmlFor="email">Email address</label>
              <input
                id="email" name="email" type="email" required autoComplete="email"
                className="input" value={form.email} onChange={handleChange}
              />
            </div>

            <div>
              <label className="label" htmlFor="password">Password</label>
              <input
                id="password" name="password" type="password" required autoComplete="current-password"
                className="input" value={form.password} onChange={handleChange}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
                Invalid email or password.
              </p>
            )}

            <button type="submit" disabled={isPending} className="btn-primary w-full py-3">
              {isPending ? 'Signing in…' : 'Sign in with email'}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-brand-700 font-semibold hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  )
}
